"use client"

import type React from "react"

import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Copy, RefreshCw, CheckCircle2, XCircle } from "lucide-react"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

function rgbToCmyk(r: number, g: number, b: number) {
  const rr = r / 255
  const gg = g / 255
  const bb = b / 255

  const k = 1 - Math.max(rr, gg, bb)
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 }

  return {
    c: Math.round(((1 - rr - k) / (1 - k)) * 100),
    m: Math.round(((1 - gg - k) / (1 - k)) * 100),
    y: Math.round(((1 - bb - k) / (1 - k)) * 100),
    k: Math.round(k * 100),
  }
}

function cmykToRgb(c: number, m: number, y: number, k: number) {
  const cc = c / 100
  const mm = m / 100
  const yy = y / 100
  const kk = k / 100

  return {
    r: Math.round(255 * (1 - cc) * (1 - kk)),
    g: Math.round(255 * (1 - mm) * (1 - kk)),
    b: Math.round(255 * (1 - yy) * (1 - kk)),
  }
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

function contrastRatio(rgb1: { r: number; g: number; b: number }, rgb2: { r: number; g: number; b: number }): number {
  const l1 = relativeLuminance(rgb1.r, rgb1.g, rgb1.b)
  const l2 = relativeLuminance(rgb2.r, rgb2.g, rgb2.b)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

function hslToRgb(h: number, s: number, l: number) {
  h /= 360
  s /= 100
  l /= 100

  let r, g, b

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  }
}

function rgbToHex(r: number, g: number, b: number) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

export function ColorConverter() {
  const [hexColor, setHexColor] = useState("#1e88e5")
  const [rgbColor, setRgbColor] = useState({ r: 30, g: 136, b: 229 })
  const [hslColor, setHslColor] = useState({ h: 210, s: 82, l: 51 })
  const [cmykColor, setCmykColor] = useState({ c: 87, m: 41, y: 0, k: 10 })
  const [activeTab, setActiveTab] = useState("hex")
  const [contrastBg, setContrastBg] = useState("#ffffff")

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) return null

    return {
      r: Number.parseInt(result[1], 16),
      g: Number.parseInt(result[2], 16),
      b: Number.parseInt(result[3], 16),
    }
  }

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }

      h /= 6
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    }
  }

  const updateAllFromRgb = (r: number, g: number, b: number) => {
    setRgbColor({ r, g, b })
    setHexColor(rgbToHex(r, g, b))
    setHslColor(rgbToHsl(r, g, b))
    setCmykColor(rgbToCmyk(r, g, b))
  }

  const updateFromHex = (hex: string) => {
    if (/^#[0-9A-F]{6}$/i.test(hex)) {
      const rgb = hexToRgb(hex)
      if (rgb) updateAllFromRgb(rgb.r, rgb.g, rgb.b)
    }
  }

  const updateFromRgb = (r: number, g: number, b: number) => {
    setHexColor(rgbToHex(r, g, b))
    setHslColor(rgbToHsl(r, g, b))
    setCmykColor(rgbToCmyk(r, g, b))
  }

  const updateFromHsl = (h: number, s: number, l: number) => {
    const rgb = hslToRgb(h, s, l)
    setRgbColor(rgb)
    setHexColor(rgbToHex(rgb.r, rgb.g, rgb.b))
    setCmykColor(rgbToCmyk(rgb.r, rgb.g, rgb.b))
  }

  const updateFromCmyk = (c: number, m: number, y: number, k: number) => {
    const rgb = cmykToRgb(c, m, y, k)
    setRgbColor(rgb)
    setHexColor(rgbToHex(rgb.r, rgb.g, rgb.b))
    setHslColor(rgbToHsl(rgb.r, rgb.g, rgb.b))
  }

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setHexColor(value)
    if (value.length === 7) updateFromHex(value)
  }

  const handleRgbChange = (component: "r" | "g" | "b", value: string) => {
    const numValue = Number.parseInt(value) || 0
    const clampedValue = Math.max(0, Math.min(255, numValue))

    const newRgb = { ...rgbColor, [component]: clampedValue }
    setRgbColor(newRgb)
    updateFromRgb(newRgb.r, newRgb.g, newRgb.b)
  }

  const handleHslChange = (component: "h" | "s" | "l", value: string) => {
    const numValue = Number.parseInt(value) || 0
    const max = component === "h" ? 360 : 100
    const clampedValue = Math.max(0, Math.min(max, numValue))

    const newHsl = { ...hslColor, [component]: clampedValue }
    setHslColor(newHsl)
    updateFromHsl(newHsl.h, newHsl.s, newHsl.l)
  }

  const handleCmykChange = (component: "c" | "m" | "y" | "k", value: string) => {
    const numValue = Number.parseInt(value) || 0
    const clampedValue = Math.max(0, Math.min(100, numValue))

    const newCmyk = { ...cmykColor, [component]: clampedValue }
    setCmykColor(newCmyk)
    updateFromCmyk(newCmyk.c, newCmyk.m, newCmyk.y, newCmyk.k)
  }

  const generateRandomColor = () => {
    const randomHex =
      "#" +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")
    setHexColor(randomHex)
    updateFromHex(randomHex)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${text} copied to clipboard!`)
  }

  const contrast = useMemo(() => {
    const bgRgb = hexToRgb(contrastBg)
    if (!bgRgb) return null
    return contrastRatio(rgbColor, bgRgb)
  }, [rgbColor, contrastBg])

  const wcagResults = useMemo(() => {
    if (!contrast) return null
    return {
      aaNormal: contrast >= 4.5,
      aaLarge: contrast >= 3,
      aaaNormal: contrast >= 7,
      aaaLarge: contrast >= 4.5,
    }
  }, [contrast])

  const palette = useMemo(() => {
    const h = hslColor.h
    const s = hslColor.s
    const l = hslColor.l
    return {
      complementary: [rgbToHex(...Object.values(hslToRgb((h + 180) % 360, s, l)) as [number, number, number])],
      analogous: [
        rgbToHex(...Object.values(hslToRgb((h + 330) % 360, s, l)) as [number, number, number]),
        hexColor,
        rgbToHex(...Object.values(hslToRgb((h + 30) % 360, s, l)) as [number, number, number]),
      ],
      triadic: [
        hexColor,
        rgbToHex(...Object.values(hslToRgb((h + 120) % 360, s, l)) as [number, number, number]),
        rgbToHex(...Object.values(hslToRgb((h + 240) % 360, s, l)) as [number, number, number]),
      ],
      shades: Array.from({ length: 5 }, (_, i) => {
        const newL = Math.max(0, l - (i + 1) * 12)
        return rgbToHex(...Object.values(hslToRgb(h, s, newL)) as [number, number, number])
      }),
      tints: Array.from({ length: 5 }, (_, i) => {
        const newL = Math.min(100, l + (i + 1) * 10)
        return rgbToHex(...Object.values(hslToRgb(h, s, newL)) as [number, number, number])
      }),
    }
  }, [hslColor, hexColor])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Color Converter</CardTitle>
          <CardDescription>
            Convert between HEX, RGB, HSL, and CMYK color formats. Check contrast and generate palettes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-4">
                  <TabsTrigger value="hex">HEX</TabsTrigger>
                  <TabsTrigger value="rgb">RGB</TabsTrigger>
                  <TabsTrigger value="hsl">HSL</TabsTrigger>
                  <TabsTrigger value="cmyk">CMYK</TabsTrigger>
                </TabsList>

                <TabsContent value="hex" className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="hex-input">HEX Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="hex-input"
                        value={hexColor}
                        onChange={handleHexChange}
                        placeholder="#000000"
                        maxLength={7}
                      />
                      <input
                        type="color"
                        value={hexColor}
                        onChange={(e) => {
                          setHexColor(e.target.value)
                          updateFromHex(e.target.value)
                        }}
                        className="h-9 w-12 rounded border cursor-pointer"
                      />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(hexColor)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="rgb" className="space-y-4 pt-4">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="rgb-r-input">R</Label>
                      <Input
                        id="rgb-r-input"
                        type="number"
                        min="0"
                        max="255"
                        value={rgbColor.r}
                        onChange={(e) => handleRgbChange("r", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rgb-g-input">G</Label>
                      <Input
                        id="rgb-g-input"
                        type="number"
                        min="0"
                        max="255"
                        value={rgbColor.g}
                        onChange={(e) => handleRgbChange("g", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rgb-b-input">B</Label>
                      <Input
                        id="rgb-b-input"
                        type="number"
                        min="0"
                        max="255"
                        value={rgbColor.b}
                        onChange={(e) => handleRgbChange("b", e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`rgb(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b})`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy RGB
                  </Button>
                </TabsContent>

                <TabsContent value="hsl" className="space-y-4 pt-4">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="hsl-h-input">H</Label>
                      <Input
                        id="hsl-h-input"
                        type="number"
                        min="0"
                        max="360"
                        value={hslColor.h}
                        onChange={(e) => handleHslChange("h", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hsl-s-input">S</Label>
                      <Input
                        id="hsl-s-input"
                        type="number"
                        min="0"
                        max="100"
                        value={hslColor.s}
                        onChange={(e) => handleHslChange("s", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hsl-l-input">L</Label>
                      <Input
                        id="hsl-l-input"
                        type="number"
                        min="0"
                        max="100"
                        value={hslColor.l}
                        onChange={(e) => handleHslChange("l", e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`hsl(${hslColor.h}, ${hslColor.s}%, ${hslColor.l}%)`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy HSL
                  </Button>
                </TabsContent>

                <TabsContent value="cmyk" className="space-y-4 pt-4">
                  <div className="grid grid-cols-4 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="cmyk-c-input">C</Label>
                      <Input
                        id="cmyk-c-input"
                        type="number"
                        min="0"
                        max="100"
                        value={cmykColor.c}
                        onChange={(e) => handleCmykChange("c", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cmyk-m-input">M</Label>
                      <Input
                        id="cmyk-m-input"
                        type="number"
                        min="0"
                        max="100"
                        value={cmykColor.m}
                        onChange={(e) => handleCmykChange("m", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cmyk-y-input">Y</Label>
                      <Input
                        id="cmyk-y-input"
                        type="number"
                        min="0"
                        max="100"
                        value={cmykColor.y}
                        onChange={(e) => handleCmykChange("y", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cmyk-k-input">K</Label>
                      <Input
                        id="cmyk-k-input"
                        type="number"
                        min="0"
                        max="100"
                        value={cmykColor.k}
                        onChange={(e) => handleCmykChange("k", e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`cmyk(${cmykColor.c}%, ${cmykColor.m}%, ${cmykColor.y}%, ${cmykColor.k}%)`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy CMYK
                  </Button>
                </TabsContent>
              </Tabs>

              <Button onClick={generateRandomColor} className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Random Color
              </Button>
            </div>

            <div className="md:w-1/3 space-y-4">
              <Label>Color Preview</Label>
              <div className="w-full aspect-square rounded-lg border" style={{ backgroundColor: hexColor }} />
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">HEX:</span>
                  <span className="font-mono">{hexColor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">RGB:</span>
                  <span className="font-mono">
                    rgb({rgbColor.r}, {rgbColor.g}, {rgbColor.b})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">HSL:</span>
                  <span className="font-mono">
                    hsl({hslColor.h}, {hslColor.s}%, {hslColor.l}%)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CMYK:</span>
                  <span className="font-mono">
                    {cmykColor.c}%, {cmykColor.m}%, {cmykColor.y}%, {cmykColor.k}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contrast Checker (WCAG)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="space-y-2 flex-1 w-full">
              <Label>Foreground Color</Label>
              <div className="flex gap-2 items-center">
                <div className="h-9 w-12 rounded border" style={{ backgroundColor: hexColor }} />
                <span className="font-mono text-sm">{hexColor}</span>
              </div>
            </div>
            <div className="space-y-2 flex-1 w-full">
              <Label>Background Color</Label>
              <div className="flex gap-2">
                <Input
                  value={contrastBg}
                  onChange={(e) => setContrastBg(e.target.value)}
                  placeholder="#ffffff"
                  maxLength={7}
                  className="font-mono"
                />
                <input
                  type="color"
                  value={contrastBg}
                  onChange={(e) => setContrastBg(e.target.value)}
                  className="h-9 w-12 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>

          {contrast && (
            <div className="space-y-3">
              <div
                className="p-4 rounded-lg border text-center"
                style={{ backgroundColor: contrastBg, color: hexColor }}
              >
                <p className="text-lg font-bold">Sample Text Preview</p>
                <p className="text-sm">This shows how your text looks on the background.</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Contrast Ratio:</span>
                <span className="font-mono font-bold">{contrast.toFixed(2)}:1</span>
              </div>

              {wcagResults && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    {wcagResults.aaNormal ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    AA Normal Text
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {wcagResults.aaLarge ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    AA Large Text
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {wcagResults.aaaNormal ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    AAA Normal Text
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {wcagResults.aaaLarge ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    AAA Large Text
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Color Palette</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Complementary</Label>
              <div className="flex gap-1 mt-1">
                <button
                  className="flex-1 h-10 rounded border cursor-pointer"
                  style={{ backgroundColor: hexColor }}
                  onClick={() => copyToClipboard(hexColor)}
                  title={hexColor}
                />
                {palette.complementary.map((c, i) => (
                  <button
                    key={i}
                    className="flex-1 h-10 rounded border cursor-pointer"
                    style={{ backgroundColor: c }}
                    onClick={() => copyToClipboard(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Analogous</Label>
              <div className="flex gap-1 mt-1">
                {palette.analogous.map((c, i) => (
                  <button
                    key={i}
                    className="flex-1 h-10 rounded border cursor-pointer"
                    style={{ backgroundColor: c }}
                    onClick={() => copyToClipboard(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Triadic</Label>
              <div className="flex gap-1 mt-1">
                {palette.triadic.map((c, i) => (
                  <button
                    key={i}
                    className="flex-1 h-10 rounded border cursor-pointer"
                    style={{ backgroundColor: c }}
                    onClick={() => copyToClipboard(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Shades (Darker)</Label>
              <div className="flex gap-1 mt-1">
                <button
                  className="flex-1 h-10 rounded border cursor-pointer"
                  style={{ backgroundColor: hexColor }}
                  onClick={() => copyToClipboard(hexColor)}
                  title={hexColor}
                />
                {palette.shades.map((c, i) => (
                  <button
                    key={i}
                    className="flex-1 h-10 rounded border cursor-pointer"
                    style={{ backgroundColor: c }}
                    onClick={() => copyToClipboard(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Tints (Lighter)</Label>
              <div className="flex gap-1 mt-1">
                <button
                  className="flex-1 h-10 rounded border cursor-pointer"
                  style={{ backgroundColor: hexColor }}
                  onClick={() => copyToClipboard(hexColor)}
                  title={hexColor}
                />
                {palette.tints.map((c, i) => (
                  <button
                    key={i}
                    className="flex-1 h-10 rounded border cursor-pointer"
                    style={{ backgroundColor: c }}
                    onClick={() => copyToClipboard(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Click any swatch to copy its hex value.</p>
        </CardContent>
      </Card>
    </div>
  )
}
